import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import JsBarcode from "jsbarcode";
import { labelStyles } from "../../styles/formStyles";
import BarcodePrintDialog from "./BarcodePrintDialog";

interface BarcodeDisplayProps {
  value: string;
  showPrint?: boolean;
  showLabel?: boolean;
  label?: string;
  width?: number;
  height?: number;
  fontSize?: number;
  margin?: number;
  showPrintText?: boolean;
  labelStyle?: object;
  showRequired?: boolean;
  useDialog?: boolean;
}

const BarcodeDisplay: React.FC<BarcodeDisplayProps> = ({
  value,
  showPrint = true,
  showLabel = true,
  label = "Barcode",
  width = 2,
  height = 60,
  fontSize = 14,
  margin = 10,
  showPrintText = false,
  labelStyle,
  showRequired = true,
  useDialog = true,
}) => {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  useEffect(() => {
    if (value && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, value, {
          format: "CODE128",
          width,
          height,
          displayValue: true,
          fontSize,
          margin,
        });
      } catch (error) {
        console.error("Error generating barcode:", error);
      }
    }
  }, [value, width, height, fontSize, margin]);

  const handlePrint = () => {
    if (useDialog) {
      setShowPrintDialog(true);
    } else {
      // Legacy direct print functionality
      if (barcodeRef.current) {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          const svgData = new XMLSerializer().serializeToString(barcodeRef.current);
          printWindow.document.write(`
            <html>
              <head>
                <title>Print Barcode</title>
                <style>
                  body { 
                    margin: 0; 
                    padding: 20px; 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    min-height: 100vh; 
                    font-family: Arial, sans-serif;
                  }
                  svg { 
                    max-width: 100%; 
                    height: auto; 
                  }
                  @media print {
                    body { padding: 10px; }
                  }
                </style>
              </head>
              <body>
                ${svgData}
              </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.print();
        }
      }
    }
  };

  if (!value) return null;

  return (
    <>
      <Box>
        {showLabel && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",}}>
            <Typography component="label" sx={labelStyle || labelStyles}>
              {label}
              {showRequired && <span style={{ color: "red" }}>*</span>}
            </Typography>
            {showPrint && (
              showPrintText ? (
                <Button
                  onClick={handlePrint}
                  startIcon={<PrintOutlinedIcon sx={{ fontSize: "18px" }} />}
                  sx={{
                    color: "primary.main",
                    textTransform: "none",
                    fontSize: "14px",
                    padding: "4px 8px",
                    minWidth: "auto",
                  }}
                >
                  Print
                </Button>
              ) : (
                <IconButton
                  onClick={handlePrint}
                  sx={{
                    color: "primary.main",
                    padding: "4px",
                  }}
                >
                  <PrintOutlinedIcon sx={{ fontSize: "18px" }} />
                </IconButton>
              )
            )}
          </Box>
        )}
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "16px",
            backgroundColor: "#fafafa",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <svg ref={barcodeRef}></svg>
        </Box>
      </Box>
      
      {useDialog && (
        <BarcodePrintDialog
          open={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
          barcodeValue={value}
        />
      )}
    </>
  );
};

export default BarcodeDisplay;